/**
 * Utility functions for managing and cleaning up Labs app localStorage
 */

// Storage key patterns used by the Labs app
const LABS_STORAGE_PATTERNS = [
  'labs-playbook-editor-',
  'labs-schema-editor-',
  'labs-playbook-edit-ui-state-',
  'labs-navbar-ui-state',
  'mr-architect-schema-editor-state-',
] as const;

// Legacy patterns that should be cleaned up
const LEGACY_STORAGE_PATTERNS = [
  'playbook-edit-state',
  'mr-architect-schema-editor-state',
] as const;

interface StorageInfo {
  key: string;
  size: number;
  lastModified?: number;
  type: 'labs' | 'legacy' | 'other';
}

/**
 * Get all localStorage keys that match Labs patterns
 */
export function getLabsStorageKeys(): string[] {
  const keys: string[] = [];
  
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (key && isLabsStorageKey(key)) {
      keys.push(key);
    }
  }
  
  return keys;
}

/**
 * Check if a key matches any Labs storage pattern
 */
export function isLabsStorageKey(key: string): boolean {
  return LABS_STORAGE_PATTERNS.some(pattern => key.startsWith(pattern)) ||
         LEGACY_STORAGE_PATTERNS.some(pattern => key.includes(pattern));
}

/**
 * Get detailed information about all storage keys
 */
export function getStorageInfo(): StorageInfo[] {
  const info: StorageInfo[] = [];
  
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (!key) continue;
    
    const value = localStorage.getItem(key);
    const size = value ? new Blob([value]).size : 0;
    
    let type: 'labs' | 'legacy' | 'other' = 'other';
    let lastModified: number | undefined;
    
    if (LABS_STORAGE_PATTERNS.some(pattern => key.startsWith(pattern))) {
      type = 'labs';
      // Try to extract timestamp from stored data
      try {
        const parsed = JSON.parse(value || '{}');
        lastModified = parsed.timestamp || parsed.lastSaved;
      } catch {
        // Ignore parsing errors
      }
    } else if (LEGACY_STORAGE_PATTERNS.some(pattern => key.includes(pattern))) {
      type = 'legacy';
    }
    
    info.push({
      key,
      size,
      lastModified,
      type,
    });
  }
  
  return info.sort((a, b) => (b.lastModified || 0) - (a.lastModified || 0));
}

/**
 * Clean up old or orphaned storage entries
 */
export function cleanupLabsStorage(options: {
  maxAge?: number; // Max age in milliseconds
  maxEntries?: number; // Max number of entries to keep per type
  removeLegacy?: boolean; // Remove all legacy entries
} = {}): {
  removed: string[];
  kept: string[];
  totalSizeFreed: number;
} {
  const {
    maxAge = 7 * 24 * 60 * 60 * 1000, // 7 days default
    maxEntries = 10, // Keep max 10 entries per editor type
    removeLegacy = true,
  } = options;
  
  const storageInfo = getStorageInfo();
  const now = Date.now();
  const removed: string[] = [];
  const kept: string[] = [];
  let totalSizeFreed = 0;
  
  // Group by editor type
  const labsEntries = storageInfo.filter(info => info.type === 'labs');
  const legacyEntries = storageInfo.filter(info => info.type === 'legacy');
  
  // Remove legacy entries if requested
  if (removeLegacy) {
    legacyEntries.forEach(info => {
      localStorage.removeItem(info.key);
      removed.push(info.key);
      totalSizeFreed += info.size;
    });
  }
  
  // Group labs entries by prefix
  const groupedEntries = new Map<string, StorageInfo[]>();
  labsEntries.forEach(info => {
    const prefix = LABS_STORAGE_PATTERNS.find(pattern => info.key.startsWith(pattern));
    if (prefix) {
      if (!groupedEntries.has(prefix)) {
        groupedEntries.set(prefix, []);
      }
      groupedEntries.get(prefix)!.push(info);
    }
  });
  
  // Clean up each group
  groupedEntries.forEach((entries) => {
    // Sort by last modified (newest first)
    entries.sort((a, b) => (b.lastModified || 0) - (a.lastModified || 0));
    
    entries.forEach((info, index) => {
      const age = info.lastModified ? now - info.lastModified : Infinity;
      const shouldRemove = age > maxAge || index >= maxEntries;
      
      if (shouldRemove) {
        localStorage.removeItem(info.key);
        removed.push(info.key);
        totalSizeFreed += info.size;
      } else {
        kept.push(info.key);
      }
    });
  });
  
  return {
    removed,
    kept,
    totalSizeFreed,
  };
}

/**
 * Clear all Labs storage (useful for debugging or reset)
 */
export function clearAllLabsStorage(): string[] {
  const keys = getLabsStorageKeys();
  keys.forEach(key => localStorage.removeItem(key));
  return keys;
}

/**
 * Get storage usage statistics
 */
export function getStorageStats(): {
  totalKeys: number;
  labsKeys: number;
  legacyKeys: number;
  totalSize: number;
  labsSize: number;
  legacySize: number;
} {
  const info = getStorageInfo();
  
  const labsEntries = info.filter(i => i.type === 'labs');
  const legacyEntries = info.filter(i => i.type === 'legacy');
  
  return {
    totalKeys: info.length,
    labsKeys: labsEntries.length,
    legacyKeys: legacyEntries.length,
    totalSize: info.reduce((sum, i) => sum + i.size, 0),
    labsSize: labsEntries.reduce((sum, i) => sum + i.size, 0),
    legacySize: legacyEntries.reduce((sum, i) => sum + i.size, 0),
  };
}

/**
 * Format bytes to human readable string
 */
export function formatBytes(bytes: number): string {
  if (bytes === 0) return '0 Bytes';
  
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
}

/**
 * Auto-cleanup function that runs on app startup
 */
export function autoCleanupStorage(): void {
  try {
    const result = cleanupLabsStorage({
      maxAge: 7 * 24 * 60 * 60 * 1000, // 7 days
      maxEntries: 5, // Keep max 5 entries per editor type
      removeLegacy: true,
    });
    
    if (result.removed.length > 0) {
      console.log(`[Labs Storage] Cleaned up ${result.removed.length} old entries, freed ${formatBytes(result.totalSizeFreed)}`);
    }
  } catch (error) {
    console.error('[Labs Storage] Auto-cleanup failed:', error);
  }
}
